import { useUserOptional, useUser } from "@/components/auth-provider";
import { Button } from "@stockline/ui/components/button";
import { Progress } from "@stockline/ui/components/progress";
import { ScrollArea } from "@stockline/ui/components/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@stockline/ui/components/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@stockline/ui/components/select";
import { uploadService, UploadTask } from "@/services/upload-service";
import {
  useExecutionsPaged as useOfflineExecutionsPaged,
  useCancelExecution as useOfflineCancelExecution,
} from "@stockline/web";
import type { AgentExecutionLight } from "@stockline/shared/types/ai/execution";
import { extractSourceType } from "@/components/ai/SourceBadge";
import { ContextEditor } from "@/components/context-editor";
import { WorkflowInstructionsBlock } from "@/components/workflow-instructions-block";
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  X,
  Bot,
  Loader2,
  Upload,
  MessageSquare,
  Mail,
  FileText,
  Receipt,
  Phone,
  Sparkles,
  Building2,
  User,
  Users,
  GripVertical,
  Save,
  Check,
} from "lucide-react";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  useCompany,
  useUpdateCompany,
  useUpdateCompanyBIContext,
} from "@/query/hooks/useCompany";
import { useFeatureFlag } from "@/query/hooks/useFeatureFlags";
import {
  useUpdateUserAIContext,
  useUpdateUserBIContext,
} from "@/query/hooks/useUser";
import { useMyGroups, useUpdateGroupAIContext } from "@/query/hooks/useGroups";
import { useGroupContextEditor } from "@/hooks/useGroupContextEditor";
import type { UUID } from "node:crypto";
import { useToast } from "@/hooks/use-toast";
import { SidePanelChatView } from "@/app/ai/components/chat/SidePanelChatView";
import { CompanyPermissions } from "@/app/api/types/superadmin";

const DISMISSED_STORAGE_KEY = "stockline_dismissed_activities";
const PANEL_OPEN_KEY = "stockline_activity_panel_open";
const PANEL_WIDTH_KEY = "stockline_activity_panel_width";
const PANEL_HEIGHT_KEY = "stockline_activity_panel_height";

const DEFAULT_PANEL_WIDTH = 440;
const EXECUTIONS_PAGE_SIZE = 50;
const MIN_PANEL_WIDTH = 360;
const MAX_PANEL_WIDTH = 720;

const DEFAULT_PANEL_HEIGHT = 620;
const MIN_PANEL_HEIGHT = 420;
const MAX_PANEL_HEIGHT = 900;

// Gap kept between the floating card / launcher and the viewport edges.
const PANEL_MARGIN = 16;

// Rough heuristic — 1 token ≈ 4 chars for English/French/Spanish.
// Display only, never a save gate.

function getDismissedIds(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const stored = localStorage.getItem(DISMISSED_STORAGE_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

function saveDismissedIds(ids: Set<string>) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // Ignore storage errors
  }
}

function getPanelOpen(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(PANEL_OPEN_KEY) === "true";
  } catch {
    return false;
  }
}

function savePanelOpen(open: boolean) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(PANEL_OPEN_KEY, String(open));
  } catch {
    // Ignore
  }
}

// Largest width/height the floating card may take on the current viewport: the
// design cap, but never more than the room available (minus a margin on each
// side) so the card can't overflow on narrow/short screens. Callers apply the
// MIN floor separately (and that floor is itself clamped to this max).
function maxPanelWidth(): number {
  return Math.min(MAX_PANEL_WIDTH, window.innerWidth - 2 * PANEL_MARGIN);
}

function maxPanelHeight(): number {
  return Math.min(MAX_PANEL_HEIGHT, window.innerHeight - 2 * PANEL_MARGIN);
}

function getPanelWidth(): number {
  if (typeof window === "undefined") return DEFAULT_PANEL_WIDTH;
  try {
    const max = maxPanelWidth();
    const min = Math.min(MIN_PANEL_WIDTH, max);
    const stored = localStorage.getItem(PANEL_WIDTH_KEY);
    if (stored) {
      const width = parseInt(stored, 10);
      if (!Number.isNaN(width)) {
        return Math.min(Math.max(width, min), max);
      }
    }
    return Math.min(DEFAULT_PANEL_WIDTH, max);
  } catch {
    return DEFAULT_PANEL_WIDTH;
  }
}

function getPanelHeight(): number {
  if (typeof window === "undefined") return DEFAULT_PANEL_HEIGHT;
  try {
    const max = maxPanelHeight();
    const min = Math.min(MIN_PANEL_HEIGHT, max);
    const stored = localStorage.getItem(PANEL_HEIGHT_KEY);
    if (stored) {
      const height = parseInt(stored, 10);
      if (!Number.isNaN(height)) {
        return Math.min(Math.max(height, min), max);
      }
    }
    return Math.min(DEFAULT_PANEL_HEIGHT, max);
  } catch {
    return DEFAULT_PANEL_HEIGHT;
  }
}

function savePanelWidth(width: number) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(PANEL_WIDTH_KEY, String(width));
  } catch {
    // Ignore
  }
}

function savePanelHeight(height: number) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(PANEL_HEIGHT_KEY, String(height));
  } catch {
    // Ignore
  }
}

type SourceIconType =
  | "whatsapp"
  | "email"
  | "sms"
  | "invoice"
  | "document"
  | "ai"
  | "upload";

function getSourceIcon(
  source?: string,
  type?: "upload" | "ai",
): SourceIconType {
  if (type === "upload") return "upload";
  if (!source) return "ai";

  const s = source.toLowerCase();
  if (s.includes("whatsapp") || s.includes("chat")) return "whatsapp";
  if (s.includes("email") || s.includes("mail")) return "email";
  if (s.includes("sms")) return "sms";
  if (s.includes("invoice") || s.includes("facture") || s.includes("bill"))
    return "invoice";
  if (s.includes("document") || s.includes("pdf")) return "document";
  return "ai";
}

function SourceIcon({
  type,
  className,
}: {
  type: SourceIconType;
  className?: string;
}) {
  const iconClass = cn("h-4 w-4", className);

  switch (type) {
    case "whatsapp":
      return <MessageSquare className={cn(iconClass, "text-green-500")} />;
    case "email":
      return <Mail className={cn(iconClass, "text-blue-500")} />;
    case "sms":
      return <Phone className={cn(iconClass, "text-purple-500")} />;
    case "invoice":
      return <Receipt className={cn(iconClass, "text-orange-500")} />;
    case "document":
      return <FileText className={cn(iconClass, "text-slate-500")} />;
    case "upload":
      return <Upload className={cn(iconClass, "text-cyan-500")} />;
    case "ai":
    default:
      return <Sparkles className={cn(iconClass, "text-violet-500")} />;
  }
}

type ActivityItem = {
  id: string;
  type: "upload" | "ai";
  name: string;
  status: "pending" | "active" | "completed" | "failed" | "cancelled";
  progress: number;
  error?: string;
  source?: string;
  sourceIcon: SourceIconType;
  details?: string;
  summary?: string | null;
  startedAt?: string;
  createdAt?: string;
};

function mapUploadToActivity(task: UploadTask): ActivityItem {
  return {
    id: `upload-${task.id}`,
    type: "upload",
    name: task.file.name,
    status:
      task.status === "completed"
        ? "completed"
        : task.status === "error"
          ? "failed"
          : task.status === "cancelled"
            ? "cancelled"
            : task.status === "uploading" || task.status === "processing"
              ? "active"
              : "pending",
    progress: task.progress,
    error: task.error,
    source: task.type,
    sourceIcon: "upload",
  };
}

const TERMINAL_STATUSES = new Set(["completed", "failed", "cancelled"]);

function mapExecutionToActivity(exec: AgentExecutionLight): ActivityItem {
  const sourceType = extractSourceType(null, exec.agentName);
  const displayName = exec.agentName || exec.agentType || "AI Execution";
  const cleanName = displayName.replace(/^\[\w+\]\s*/, "");

  const statusMap: Record<string, ActivityItem["status"]> = {
    completed: "completed",
    failed: "failed",
    cancelled: "cancelled",
    running: "active",
    pending: "pending",
  };

  return {
    id: `ai-${exec.id}`,
    type: "ai",
    name: cleanName,
    status: statusMap[exec.status] ?? "pending",
    progress: TERMINAL_STATUSES.has(exec.status)
      ? 100
      : exec.status === "running"
        ? 50
        : 10,
    error: exec.errorMessage || undefined,
    source: sourceType || exec.agentType,
    sourceIcon: getSourceIcon(sourceType || exec.agentType, "ai"),
    details: undefined,
    summary: exec.summary ?? null,
    startedAt: exec.startedAt || undefined,
    createdAt: exec.createdAt,
  };
}

function ActivityItemRow({
  item,
  onDismiss,
  onCancel,
  expanded,
  onToggleExpand,
}: {
  item: ActivityItem;
  onDismiss: () => void;
  onCancel?: () => void;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const isActive = item.status === "active" || item.status === "pending";
  const expandedText =
    item.status === "cancelled"
      ? null
      : (item.error ?? item.details ?? item.summary);
  const canExpand = Boolean(expandedText);
  const expandedTextClass = item.error
    ? "text-red-600"
    : "text-muted-foreground";

  return (
    <div className="px-3 py-2.5 border-b border-border/50 hover:bg-muted/30 transition-colors overflow-hidden">
      <div className="flex items-start gap-2.5 min-w-0">
        {/* Clickable row area - clicking anywhere here toggles expand */}
        <div
          className={cn(
            "flex items-start gap-2.5 flex-1 min-w-0",
            canExpand && "cursor-pointer",
          )}
          onClick={canExpand ? onToggleExpand : undefined}
        >
          <div className="mt-0.5">
            <SourceIcon type={item.sourceIcon} />
          </div>
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className={cn("flex-shrink-0", !canExpand && "invisible")}
                aria-hidden={!canExpand}
              >
                {expanded ? (
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                )}
              </div>
              <span
                className="text-sm font-medium truncate min-w-0 flex-1"
                title={item.name}
              >
                {item.name}
              </span>
              <div className="flex items-center gap-1 flex-shrink-0">
                {item.status === "completed" && (
                  <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                )}
                {item.status === "failed" && (
                  <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                )}
                {item.status === "cancelled" && (
                  <AlertCircle className="h-3.5 w-3.5 text-orange-500" />
                )}
                {isActive && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
                )}
              </div>
            </div>

            {isActive && (
              <Progress value={item.progress} className="h-1 mt-1.5" />
            )}

            {expanded && expandedText && (
              <div className="mt-2 text-xs bg-muted/50 p-2 rounded border">
                <p
                  className={cn(
                    expandedTextClass,
                    "whitespace-pre-wrap break-words",
                  )}
                >
                  {expandedText}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons - separate from clickable area */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {isActive && onCancel && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 hover:bg-red-100 hover:text-red-600"
              onClick={onCancel}
              title="Cancel"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
          {!isActive && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 hover:bg-muted"
              onClick={onDismiss}
              title="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Auto-resizing textarea — grows with content within [min,max] bounds.
function useAutosize(
  value: string,
  { minHeight, maxHeight }: { minHeight: number; maxHeight: number },
) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(Math.max(el.scrollHeight, minHeight), maxHeight);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [value, minHeight, maxHeight]);
  return ref;
}

function HeaderActions({
  isDirty,
  isSaving,
  isLoading = false,
  canSave,
  onSave,
  onDiscard,
}: {
  isDirty: boolean;
  isSaving: boolean;
  isLoading?: boolean;
  canSave: boolean;
  onSave: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  if (isLoading) return null;
  if (!isDirty && !isSaving) {
    return (
      <div className="flex items-center shrink-0 pr-1">
        <span className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-muted/50 px-2.5 text-xs text-muted-foreground">
          <Check className="h-3 w-3" />
          {t("common.saved")}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 shrink-0 pr-1">
      <Button
        size="sm"
        variant="ghost"
        onClick={onDiscard}
        disabled={!isDirty || isSaving}
        className="h-7 px-2.5 text-xs"
      >
        {t("ui.discard")}
      </Button>
      <Button
        size="sm"
        onClick={onSave}
        disabled={!canSave}
        className="h-7 px-2.5 gap-1.5 text-xs"
      >
        {isSaving ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Save className="h-3 w-3" />
        )}
        {t("common.save")}
      </Button>
    </div>
  );
}

// Context Tab Content
export function ContextTabContent({
  layout = "stacked",
  active = true,
  includeBiContext = false,
  includeWorkflowInstructions = true,
}: {
  layout?: "stacked" | "grid";
  active?: boolean;
  /** Render the BI-context section. Off on the user-facing /ai
   *  context tab; on only in the hidden /ai-admin Context tab. */
  includeBiContext?: boolean;
  /** Render the per-workflow instructions tier. Off on the
   *  user-facing /ai context tab; on everywhere else. */
  includeWorkflowInstructions?: boolean;
} = {}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const user = useUser();
  const { data: company } = useCompany();
  const { data: myGroups } = useMyGroups({
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: active ? 5_000 : false,
  });
  const updateCompanyMutation = useUpdateCompany();
  const updateUserAIContextMutation = useUpdateUserAIContext();
  const updateUserBIContextMutation = useUpdateUserBIContext();
  const updateCompanyBIContextMutation = useUpdateCompanyBIContext();
  const updateGroupAIContextMutation = useUpdateGroupAIContext();

  const [companyContextOpen, setCompanyContextOpen] = useState(true);
  const [userContextOpen, setUserContextOpen] = useState(true);
  const [groupContextOpen, setGroupContextOpen] = useState(true);
  const [biContextOpen, setBiContextOpen] = useState(true);
  const [companyBiContextOpen, setCompanyBiContextOpen] = useState(true);
  const [companyContext, setCompanyContext] = useState("");
  const [userContext, setUserContext] = useState("");
  const [biContext, setBiContext] = useState("");
  const [companyBiContext, setCompanyBiContext] = useState("");
  const [companyContextDirty, setCompanyContextDirty] = useState(false);
  const [userContextDirty, setUserContextDirty] = useState(false);
  const [biContextDirty, setBiContextDirty] = useState(false);
  const [companyBiContextDirty, setCompanyBiContextDirty] = useState(false);
  const canEditCompany = user.company_permissions === CompanyPermissions.OWNER;

  // Sync state when data loads
  useEffect(() => {
    if (company?.ai_context !== undefined && !companyContextDirty) {
      setCompanyContext(company.ai_context || "");
    }
  }, [company?.ai_context, companyContextDirty]);

  useEffect(() => {
    if (user.ai_context !== undefined && !userContextDirty) {
      setUserContext(user.ai_context || "");
    }
  }, [user.ai_context, userContextDirty]);

  useEffect(() => {
    if (user.bi_context !== undefined && !biContextDirty) {
      setBiContext(user.bi_context || "");
    }
  }, [user.bi_context, biContextDirty]);

  useEffect(() => {
    if (company?.bi_context !== undefined && !companyBiContextDirty) {
      setCompanyBiContext(company.bi_context || "");
    }
  }, [company?.bi_context, companyBiContextDirty]);

  const handleCompanyContextChange = (value: string) => {
    setCompanyContext(value);
    setCompanyContextDirty(value !== (company?.ai_context || ""));
  };

  const handleUserContextChange = (value: string) => {
    setUserContext(value);
    setUserContextDirty(value !== (user.ai_context || ""));
  };

  const handleDiscardCompanyContext = () => {
    setCompanyContext(company?.ai_context || "");
    setCompanyContextDirty(false);
  };

  const handleDiscardUserContext = () => {
    setUserContext(user.ai_context || "");
    setUserContextDirty(false);
  };

  const handleSaveCompanyContext = async () => {
    try {
      await updateCompanyMutation.mutateAsync({
        ai_context: companyContext || null,
      });
      setCompanyContextDirty(false);
      toast({ title: t("common.success") });
    } catch (error) {
      console.error("Failed to save company AI context:", error);
      toast({
        title: t("common.error"),
        variant: "destructive",
      });
    }
  };

  const handleSaveUserContext = async () => {
    try {
      await updateUserAIContextMutation.mutateAsync(userContext || null);
      setUserContextDirty(false);
      toast({ title: t("common.success") });
    } catch (error) {
      console.error("Failed to save user AI context:", error);
      toast({
        title: t("common.error"),
        variant: "destructive",
      });
    }
  };

  const handleBiContextChange = (value: string) => {
    setBiContext(value);
    setBiContextDirty(value !== (user.bi_context || ""));
  };

  const handleDiscardBiContext = () => {
    setBiContext(user.bi_context || "");
    setBiContextDirty(false);
  };

  const handleSaveBiContext = async () => {
    try {
      await updateUserBIContextMutation.mutateAsync(biContext || null);
      setBiContextDirty(false);
      toast({ title: t("common.success") });
    } catch (error) {
      console.error("Failed to save user BI context:", error);
      toast({
        title: t("common.error"),
        variant: "destructive",
      });
    }
  };

  const handleCompanyBiContextChange = (value: string) => {
    setCompanyBiContext(value);
    setCompanyBiContextDirty(value !== (company?.bi_context || ""));
  };

  const handleDiscardCompanyBiContext = () => {
    setCompanyBiContext(company?.bi_context || "");
    setCompanyBiContextDirty(false);
  };

  const handleSaveCompanyBiContext = async () => {
    try {
      await updateCompanyBIContextMutation.mutateAsync(
        companyBiContext || null,
      );
      setCompanyBiContextDirty(false);
      toast({ title: t("common.success") });
    } catch (error) {
      console.error("Failed to save company BI context:", error);
      toast({
        title: t("common.error"),
        variant: "destructive",
      });
    }
  };

  // Group AI context editing (owner/admin per group), shared with the
  // general-context dialog via a common hook.
  const {
    groupsById,
    selectedGroupId,
    setSelectedGroupId,
    setGroupEdit,
    resetGroupEdits,
    canEditSelectedGroup,
    canAdministerSelectedGroup,
    currentGroupValue,
    selectedGroupDirty,
    collectDirtyEditableEdits,
  } = useGroupContextEditor(myGroups, user.id);

  const handleGroupContextChange = (value: string) => {
    if (!selectedGroupId) return;
    setGroupEdit(selectedGroupId, value);
  };

  const handleDiscardGroupContext = () => {
    resetGroupEdits();
  };

  const handleSaveGroupContext = async () => {
    try {
      const dirty = collectDirtyEditableEdits();
      if (dirty.length === 0) return;
      await Promise.all(
        dirty.map(([groupId, value]) =>
          updateGroupAIContextMutation.mutateAsync({
            groupId: groupId as UUID,
            ai_context: value || null,
          }),
        ),
      );
      resetGroupEdits();
      toast({ title: t("common.success") });
    } catch (error) {
      console.error("Failed to save group AI context:", error);
      toast({
        title: t("common.error"),
        variant: "destructive",
      });
    }
  };

  // `border-y border-border/40 py-2` gives each context block a subtle
  // top + bottom separator so the three cards (general / my / group)
  // read as distinct surfaces inside the otherwise flat side panel.
  //
  // NB: no `lg:h-full`/`lg:flex-1` stretch chain here. Forcing the card to
  // `height:100%` of its grid row makes it clamp SHORTER than its own content
  // (the preview's `max-h-[200px]` + circular flex sizing), so a long context
  // (e.g. a group card) overflows its box and spills over the section below.
  // Equal-height cards in a row come for free from the grid's default
  // `align-items: stretch`; the explicit stretch chain only caused overflow.
  const collapsibleClass = "border-y border-border/40 py-2";
  const collapsibleContentClass = "pt-2";

  // Per-section save buttons in the panel are gone — saving happens
  // exclusively from inside the editor dialog now, so we no longer need
  // separate `canSaveX` predicates here. ContextEditor reads `isDirty`
  // and `isSaving` directly and computes its own canSave.
  const hasGroups = (myGroups?.length ?? 0) > 0;

  return (
    <div className={cn("p-3", layout === "grid" ? "space-y-4" : "space-y-3")}>
      {/* General context tier — company / user / (bi) / group cards. Kept in
          its own grid so the workflow tier below can never collide with a
          col-span sibling or a stretched (`h-full`) group card (STK-1432). */}
      <div
        className={cn(
          layout === "grid"
            ? "grid grid-cols-1 lg:grid-cols-2 gap-3"
            : "space-y-3",
        )}
      >
        {/* Company Context — visible to all, editable only by OWNER */}
        <Collapsible
          open={companyContextOpen}
          onOpenChange={setCompanyContextOpen}
          className={collapsibleClass}
        >
          <div className="flex items-center gap-2">
            <CollapsibleTrigger className="flex flex-1 min-w-0 items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-500/10">
                  <Building2 className="h-5 w-5 text-blue-500" />
                </span>
                <span className="text-sm font-medium truncate">
                  {t("common.aiManagement.companyContext")}
                </span>
                {companyContextDirty && (
                  <span
                    className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                    title={t("ui.unsavedChanges")}
                    aria-label={t("ui.unsavedChanges")}
                  />
                )}
              </div>
              {companyContextOpen ? (
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
            </CollapsibleTrigger>
            {/* Per-section Save lives inside the expand editor dialog now;
              the panel header just signals dirty state via the dot. */}
          </div>
          <CollapsibleContent className={collapsibleContentClass}>
            <ContextEditor
              value={companyContext}
              onChange={handleCompanyContextChange}
              onSave={handleSaveCompanyContext}
              onDiscard={handleDiscardCompanyContext}
              isDirty={companyContextDirty}
              isSaving={updateCompanyMutation.isPending}
              isReadOnly={!canEditCompany}
              title={t("common.aiManagement.companyContext")}
              description={
                canEditCompany
                  ? t("common.aiManagement.companyContextDescription")
                  : t("common.aiManagement.noPermission")
              }
              placeholder={t("common.aiManagement.companyContextPlaceholder")}
              layout={layout}
            />
          </CollapsibleContent>
        </Collapsible>

        {/* User Context */}
        <Collapsible
          open={userContextOpen}
          onOpenChange={setUserContextOpen}
          className={collapsibleClass}
        >
          <div className="flex items-center gap-2">
            <CollapsibleTrigger className="flex flex-1 min-w-0 items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-500/10">
                  <User className="h-5 w-5 text-green-500" />
                </span>
                <span className="text-sm font-medium truncate">
                  {t("common.aiManagement.userContext")}
                </span>
                {userContextDirty && (
                  <span
                    className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                    title={t("ui.unsavedChanges")}
                    aria-label={t("ui.unsavedChanges")}
                  />
                )}
              </div>
              {userContextOpen ? (
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
            </CollapsibleTrigger>
            {/* Save now lives inside the editor dialog. */}
          </div>
          <CollapsibleContent className={collapsibleContentClass}>
            <ContextEditor
              value={userContext}
              onChange={handleUserContextChange}
              onSave={handleSaveUserContext}
              onDiscard={handleDiscardUserContext}
              isDirty={userContextDirty}
              isSaving={updateUserAIContextMutation.isPending}
              title={t("common.aiManagement.userContext")}
              description={t("common.aiManagement.userContextDescription")}
              placeholder={t("common.aiManagement.userContextPlaceholder")}
              layout={layout}
            />
          </CollapsibleContent>
        </Collapsible>

        {/* Company BI Context — company-wide persistent instructions injected
          into every user's BI-mode chats, ahead of each user's personal BI
          context. Rendered solely where explicitly enabled (the hidden
          /ai-admin Context tab); the write is not role-gated yet, so this
          surface is staff-facing for now. */}
        {includeBiContext && (
          <Collapsible
            open={companyBiContextOpen}
            onOpenChange={setCompanyBiContextOpen}
            className={collapsibleClass}
          >
            <div className="flex items-center gap-2">
              <CollapsibleTrigger className="flex flex-1 min-w-0 items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-500/10">
                    <Building2 className="h-5 w-5 text-amber-500" />
                  </span>
                  <span className="text-sm font-medium truncate">
                    {t("common.aiManagement.companyBiContext")}
                  </span>
                  {companyBiContextDirty && (
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                      title={t("ui.unsavedChanges")}
                      aria-label={t("ui.unsavedChanges")}
                    />
                  )}
                </div>
                {companyBiContextOpen ? (
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent className={collapsibleContentClass}>
              <ContextEditor
                value={companyBiContext}
                onChange={handleCompanyBiContextChange}
                onSave={handleSaveCompanyBiContext}
                onDiscard={handleDiscardCompanyBiContext}
                isDirty={companyBiContextDirty}
                isSaving={updateCompanyBIContextMutation.isPending}
                title={t("common.aiManagement.companyBiContext")}
                description={t(
                  "common.aiManagement.companyBiContextDescription",
                )}
                placeholder={t(
                  "common.aiManagement.companyBiContextPlaceholder",
                )}
                layout={layout}
              />
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* BI Context — self-only persistent instructions injected
          into BI-mode chats only. Rendered solely where explicitly enabled
          (the hidden /ai-admin Context tab), never on the user-facing /ai
          context tab. */}
        {includeBiContext && (
          <Collapsible
            open={biContextOpen}
            onOpenChange={setBiContextOpen}
            className={collapsibleClass}
          >
            <div className="flex items-center gap-2">
              <CollapsibleTrigger className="flex flex-1 min-w-0 items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-500/10">
                    <Sparkles className="h-5 w-5 text-amber-500" />
                  </span>
                  <span className="text-sm font-medium truncate">
                    {t("common.aiManagement.biContext")}
                  </span>
                  {biContextDirty && (
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                      title={t("ui.unsavedChanges")}
                      aria-label={t("ui.unsavedChanges")}
                    />
                  )}
                </div>
                {biContextOpen ? (
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent className={collapsibleContentClass}>
              <ContextEditor
                value={biContext}
                onChange={handleBiContextChange}
                onSave={handleSaveBiContext}
                onDiscard={handleDiscardBiContext}
                isDirty={biContextDirty}
                isSaving={updateUserBIContextMutation.isPending}
                title={t("common.aiManagement.biContext")}
                description={t("common.aiManagement.biContextDescription")}
                placeholder={t("common.aiManagement.biContextPlaceholder")}
                layout={layout}
              />
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Group Context — shared across all members of the selected group.
          Hidden entirely when the user is in no group: there's nothing to
          configure and the empty-state row added noise to the panel. */}
        {hasGroups && (
          <Collapsible
            open={groupContextOpen}
            onOpenChange={setGroupContextOpen}
            className={collapsibleClass}
            data-testid="ai-group-context"
          >
            <div className="flex items-center gap-2">
              <CollapsibleTrigger className="flex flex-1 min-w-0 items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-500/10">
                    <Users className="h-5 w-5 text-violet-500" />
                  </span>
                  <span className="text-sm font-medium truncate">
                    {t("common.aiManagement.groupContext")}
                  </span>
                  {selectedGroupDirty && (
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                      title={t("ui.unsavedChanges")}
                      aria-label={t("ui.unsavedChanges")}
                    />
                  )}
                </div>
                {groupContextOpen ? (
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
              </CollapsibleTrigger>
              {/* Save now lives inside the editor dialog. */}
            </div>
            <CollapsibleContent className={collapsibleContentClass}>
              {hasGroups ? (
                <div className="space-y-2">
                  <div className="px-1">
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      {t("common.aiManagement.groupPicker")}
                    </span>
                    <Select
                      value={selectedGroupId ?? undefined}
                      onValueChange={(v) => setSelectedGroupId(v)}
                    >
                      <SelectTrigger
                        className="mt-1 h-8 text-sm"
                        data-testid="ai-group-context-picker"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(myGroups ?? []).map((g) => (
                          <SelectItem
                            key={g.id}
                            value={g.id}
                            data-testid={`ai-group-context-option-${g.id}`}
                          >
                            {g.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedGroupId && (
                    <ContextEditor
                      value={currentGroupValue}
                      onChange={handleGroupContextChange}
                      onSave={handleSaveGroupContext}
                      onDiscard={handleDiscardGroupContext}
                      isDirty={selectedGroupDirty}
                      isSaving={updateGroupAIContextMutation.isPending}
                      isReadOnly={!canEditSelectedGroup}
                      title={`${t("common.aiManagement.groupContext")} — ${
                        groupsById.get(selectedGroupId)?.name ?? ""
                      }`}
                      description={
                        canEditSelectedGroup
                          ? t("common.aiManagement.groupContextDescription")
                          : t("common.aiManagement.noPermission")
                      }
                      placeholder={t(
                        "common.aiManagement.groupContextPlaceholder",
                      )}
                      layout={layout}
                    />
                  )}
                </div>
              ) : (
                <p className="px-2 py-3 text-xs text-muted-foreground">
                  {t("common.aiManagement.noGroups")}
                </p>
              )}
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>

      {/* Per-workflow instructions — a native tier below the general context
          cards, mirroring the company/user/group cards for a picked workflow.
          Replaces the removed GeneralContextDialog bolt-on (STK-1432).
          Group-scope instructions are a DIFFERENT resource from the group's
          aiContext — the V2 auth admits owner/admin only — so the group card
          gates on administration, not on the wider context-edit grant. */}
      {includeWorkflowInstructions && (
        <WorkflowInstructionsBlock
          userId={user.id}
          canEditCompany={canEditCompany}
          hasGroups={hasGroups}
          selectedGroupId={selectedGroupId}
          selectedGroupName={
            selectedGroupId
              ? (groupsById.get(selectedGroupId)?.name ?? null)
              : null
          }
          canEditSelectedGroup={canAdministerSelectedGroup}
          layout={layout}
        />
      )}
    </div>
  );
}

function getActivityRowCancelHandler(
  item: ActivityItem,
  cancelExecution: (id: string) => void,
): (() => void) | undefined {
  const isCancelable = item.status === "active" || item.status === "pending";
  if (!isCancelable) return undefined;
  if (item.type === "ai") {
    return () => cancelExecution(item.id.replace("ai-", ""));
  }
  if (item.type === "upload") {
    return () => uploadService.removeTask(item.id.replace("upload-", ""));
  }
  return undefined;
}

// Shared UI-state for any activity-list view: upload-task subscription,
// localStorage-backed dismissed ids (with cross-component broadcast), and
// per-row expand toggle. Execution sources are intentionally NOT bundled —
// /ai page reads the full list, the side-panel uses paged Dexie reads.
function useActivityListUiState() {
  const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    setDismissedIds(getDismissedIds());
  }, []);

  useEffect(() => {
    const unsubscribe = uploadService.subscribe(setUploadTasks);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleDismissedChange = () => {
      setDismissedIds(getDismissedIds());
    };
    globalThis.addEventListener("dismissedIdsChanged", handleDismissedChange);
    return () => {
      globalThis.removeEventListener(
        "dismissedIdsChanged",
        handleDismissedChange,
      );
    };
  }, []);

  const dismiss = useCallback((id: string) => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveDismissedIds(next);
      queueMicrotask(() => {
        globalThis.dispatchEvent(new CustomEvent("dismissedIdsChanged"));
      });
      return next;
    });
  }, []);

  const toggleExpand = useCallback((id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return {
    uploadTasks,
    dismissedIds,
    setDismissedIds,
    expandedItems,
    dismiss,
    toggleExpand,
  };
}

// Merge upload tasks + filtered executions into a single sorted activity list.
// Active rows float to the top, then newest-first.
function buildActivityList(opts: {
  uploadTasks: UploadTask[];
  executions: AgentExecutionLight[] | null | undefined;
  dismissedIds: Set<string>;
  includeExecution: (exec: AgentExecutionLight) => boolean;
}): ActivityItem[] {
  const items: ActivityItem[] = [];

  opts.uploadTasks.forEach((task) => {
    const item = mapUploadToActivity(task);
    if (!opts.dismissedIds.has(item.id)) {
      items.push(item);
    }
  });

  (opts.executions || []).forEach((exec) => {
    if (!opts.includeExecution(exec)) return;
    const item = mapExecutionToActivity(exec);
    if (!opts.dismissedIds.has(item.id)) {
      items.push(item);
    }
  });

  return items.sort((a, b) => {
    const aActive = a.status === "active" || a.status === "pending";
    const bActive = b.status === "active" || b.status === "pending";
    if (aActive !== bActive) return aActive ? -1 : 1;
    const aDate = a.createdAt || a.startedAt || "";
    const bDate = b.createdAt || b.startedAt || "";
    return bDate.localeCompare(aDate);
  });
}

interface VirtualizedActivityListProps {
  activities: ActivityItem[];
  hasMore?: boolean;
  onLoadMore?: () => void;
  expandedItems: Set<string>;
  onDismiss: (id: string) => void;
  onToggleExpand: (id: string) => void;
  cancelExecution: (id: string) => void;
}

// Virtualized, infinite-scrolling activity list. Resolves its scroll viewport
// via `closest([data-radix-scroll-area-viewport])`, so the caller must wrap
// this in a Radix `<ScrollArea>` with a bounded height. The trailing sentinel
// triggers `onLoadMore` as it scrolls into view; `hasMore` flipping false on
// the next render guards against double-firing while the next page loads.
function VirtualizedActivityList({
  activities,
  hasMore = false,
  onLoadMore,
  expandedItems,
  onDismiss,
  onToggleExpand,
  cancelExecution,
}: VirtualizedActivityListProps) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);

  const itemCount = activities.length + (hasMore ? 1 : 0);
  const virtualizer = useVirtualizer({
    count: itemCount,
    getScrollElement: () =>
      listRef.current?.closest(
        "[data-radix-scroll-area-viewport]",
      ) as Element | null,
    estimateSize: () => 60,
    overscan: 6,
    getItemKey: (index) =>
      index >= activities.length ? "sentinel" : activities[index].id,
  });
  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    if (!hasMore || !onLoadMore) return;
    const last = virtualItems[virtualItems.length - 1];
    if (last && last.index >= itemCount - 1) {
      onLoadMore();
    }
  }, [virtualItems, hasMore, itemCount, onLoadMore]);

  if (activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <Sparkles className="h-10 w-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm text-muted-foreground">
          {t("common.aiManagement.noRecentActivity")}
        </p>
        <p className="text-xs text-muted-foreground/70 mt-1">
          {t("common.aiManagement.tasksWillAppear")}
        </p>
      </div>
    );
  }

  return (
    <div
      ref={listRef}
      className="relative w-full"
      style={{ height: virtualizer.getTotalSize() }}
    >
      {virtualItems.map((virtualItem) => {
        const isSentinel = virtualItem.index >= activities.length;
        const item = isSentinel ? null : activities[virtualItem.index];
        return (
          <div
            key={virtualItem.key}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
            className="absolute left-0 top-0 w-full"
            style={{
              transform: `translateY(${virtualItem.start}px)`,
            }}
          >
            {isSentinel ? (
              <div className="flex items-center justify-center py-3">
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground/60" />
              </div>
            ) : item ? (
              <div className="group overflow-hidden">
                <ActivityItemRow
                  item={item}
                  onDismiss={() => onDismiss(item.id)}
                  onCancel={getActivityRowCancelHandler(item, cancelExecution)}
                  expanded={expandedItems.has(item.id)}
                  onToggleExpand={() => onToggleExpand(item.id)}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// Self-contained execution list view (activities + dismiss/cancel/expand state).
// Used on the /ai page; the side panel uses VirtualizedActivityList directly.
export function ExecutionListContent() {
  const { uploadTasks, dismissedIds, expandedItems, dismiss, toggleExpand } =
    useActivityListUiState();

  const [executionsLimit, setExecutionsLimit] = useState(EXECUTIONS_PAGE_SIZE);
  const executions = useOfflineExecutionsPaged(executionsLimit);
  const { cancel: cancelExecution } = useOfflineCancelExecution();
  const hasMoreExecutions = executions.length >= executionsLimit;

  const activities = useMemo(() => {
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    return buildActivityList({
      uploadTasks,
      executions,
      dismissedIds,
      // Hide chat-turn executions from the user's activity list — every chat
      // message spawns one, which is noise the user doesn't need (they have the
      // conversation itself). The internal ai-admin Agent Status list (a
      // separate V2 feed) still shows them.
      includeExecution: (exec) =>
        exec.createdAt >= oneDayAgo && exec.agentType !== "chat",
    });
  }, [uploadTasks, executions, dismissedIds]);

  const handleLoadMore = useCallback(() => {
    setExecutionsLimit((l) => l + EXECUTIONS_PAGE_SIZE);
  }, []);

  return (
    <VirtualizedActivityList
      activities={activities}
      hasMore={hasMoreExecutions}
      onLoadMore={handleLoadMore}
      expandedItems={expandedItems}
      onDismiss={dismiss}
      onToggleExpand={toggleExpand}
      cancelExecution={cancelExecution}
    />
  );
}

function ActivityPanelContent({
  suppressed = false,
}: { suppressed?: boolean } = {}) {
  const { t } = useTranslation();
  const aiPageEnabled = useFeatureFlag("sidebarShowAI") !== false;
  const [isOpen, setIsOpen] = useState(false);
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const [panelHeight, setPanelHeight] = useState(DEFAULT_PANEL_HEIGHT);
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Load panel-specific state from localStorage on mount
  useEffect(() => {
    setIsOpen(getPanelOpen());
    setPanelWidth(getPanelWidth());
    setPanelHeight(getPanelHeight());
  }, []);

  // Listen for toggle events from the floating launcher (same tab) and other
  // tabs, keeping the card in sync with the open flag.
  useEffect(() => {
    const handleToggle = (e: CustomEvent<boolean>) => {
      setIsOpen(e.detail);
    };
    globalThis.addEventListener(
      "activityPanelToggle",
      handleToggle as EventListener,
    );
    return () =>
      globalThis.removeEventListener(
        "activityPanelToggle",
        handleToggle as EventListener,
      );
  }, []);

  // Listen for the open flag changing in other tabs (the same-tab
  // `activityPanelToggle` CustomEvent does not cross tabs, so without this the
  // card and the launcher — which already watches `storage` — desync: the
  // launcher could hide while the card stays closed, leaving no way to open it.
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === PANEL_OPEN_KEY) {
        setIsOpen(e.newValue === "true");
      }
    };
    globalThis.addEventListener("storage", handleStorageChange);
    return () => globalThis.removeEventListener("storage", handleStorageChange);
  }, []);

  const togglePanel = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      savePanelOpen(next);
      // Notify the floating launcher so it stays in sync.
      queueMicrotask(() => {
        globalThis.dispatchEvent(
          new CustomEvent("activityPanelToggle", { detail: next }),
        );
      });
      return next;
    });
  }, []);

  // On the chat-owning routes (/ai, /steering) the card stays MOUNTED — so its
  // chat (selected conversation, unsent composer text, resize) survives the
  // navigation — but is force-hidden so it can't overlap those surfaces' own
  // chat. `visible` folds that route-level suppression into the open flag; the
  // persisted open flag itself is untouched, so the card reappears in its
  // prior state when you return to a normal route.
  const visible = isOpen && !suppressed;

  // Close on Escape while visible (standard dialog affordance).
  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") togglePanel();
    };
    globalThis.addEventListener("keydown", onKeyDown);
    return () => globalThis.removeEventListener("keydown", onKeyDown);
  }, [visible, togglePanel]);

  // On open, move keyboard focus into the card's composer so a dialog user can
  // start typing immediately (without this, focus stays on the page behind, or
  // on the launcher that just vanished). Deferred a frame so the card's `inert`
  // is cleared and it has painted first. Focus is returned to the launcher on
  // close by the launcher itself.
  useEffect(() => {
    if (!visible) return;
    const id = requestAnimationFrame(() => {
      panelRef.current
        ?.querySelector<HTMLElement>('[data-testid="chat-message-input"]')
        ?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [visible]);

  // Resize handlers — the floating card is anchored to the bottom-right
  // corner, so the grip lives at its TOP-LEFT corner and drags both
  // dimensions at once: moving left widens, moving up heightens.
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Distance from the cursor to the card's anchored (right/bottom) edges,
      // minus the viewport margin, is the new size. Clamp to the design
      // bounds, themselves capped to the viewport so the card never overflows.
      const maxW = maxPanelWidth();
      const minW = Math.min(MIN_PANEL_WIDTH, maxW);
      const newWidth = window.innerWidth - PANEL_MARGIN - e.clientX;
      setPanelWidth(Math.max(minW, Math.min(maxW, newWidth)));

      const maxH = maxPanelHeight();
      const minH = Math.min(MIN_PANEL_HEIGHT, maxH);
      const newHeight = window.innerHeight - PANEL_MARGIN - e.clientY;
      setPanelHeight(Math.max(minH, Math.min(maxH, newHeight)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      // Read the latest dimensions via the functional-update form rather than
      // the closure's `panelWidth`/`panelHeight`: `mouseup` nearly always fires
      // before React has committed the re-render from the final `mousemove`, so
      // the closed-over values are one drag-step stale.
      setPanelWidth((w) => {
        savePanelWidth(w);
        return w;
      });
      setPanelHeight((h) => {
        savePanelHeight(h);
        return h;
      });
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "nwse-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    // Listeners read live state via functional updates, so the effect only
    // needs to re-run when resizing toggles — not on every drag-step re-render.
  }, [isResizing]);

  // Keep the card within the viewport when it shrinks (e.g. window resize or
  // rotation) — re-clamp the persisted dimensions to the new bounds.
  useEffect(() => {
    const onWindowResize = () => {
      setPanelWidth((w) => {
        const maxW = maxPanelWidth();
        return Math.min(Math.max(w, Math.min(MIN_PANEL_WIDTH, maxW)), maxW);
      });
      setPanelHeight((h) => {
        const maxH = maxPanelHeight();
        return Math.min(Math.max(h, Math.min(MIN_PANEL_HEIGHT, maxH)), maxH);
      });
    };
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, []);

  return (
    <>
      {/* Floating card — anchored to the bottom-right corner (STK-356). A
          chat-only surface: executions and AI context live on the full AI page
          (the maximize link), not here. Its width/height clamp to the viewport
          (minus a margin), so on a phone it becomes a near-full-bleed sheet
          while on desktop it's the resizable card. Hidden (not unmounted) when
          closed so its chat/session state survives a reopen. */}
      <div
        ref={panelRef}
        className={cn(
          "fixed bottom-2 right-2 sm:bottom-4 sm:right-4 z-[100]",
          "bg-background border rounded-lg shadow-2xl flex flex-col overflow-hidden",
          "origin-bottom-right transition-[opacity,transform] duration-200 ease-out",
          visible
            ? "opacity-100 scale-100 pointer-events-auto"
            : "opacity-0 scale-95 pointer-events-none",
        )}
        style={{
          width: `min(${panelWidth}px, calc(100vw - ${2 * PANEL_MARGIN}px))`,
          height: `min(${panelHeight}px, calc(100vh - ${2 * PANEL_MARGIN}px))`,
        }}
        // Only a dialog when actually open. The card stays mounted (inert) when
        // closed for transition/focus reasons, but a closed, inert panel must
        // not advertise itself as a dialog — otherwise it pollutes every
        // `[role="dialog"]` / `getByRole("dialog")` query app-wide (e.g. a
        // feature's own modal can no longer be located unambiguously).
        // Intentionally NOT aria-modal: this is a non-blocking, dockable
        // assistant — there is no backdrop and the page behind stays fully
        // interactive, so claiming modality would mislead assistive tech.
        role={visible ? "dialog" : undefined}
        aria-label={t("common.aiManagement.title")}
        // `inert` (not just `aria-hidden`) so the closed-but-mounted card's
        // chat inputs/buttons are removed from the tab order and AT tree —
        // avoids focus landing inside a visually hidden panel.
        inert={!visible}
        data-testid="ai-assistant-card"
      >
        {/* Resize Handle — top-left corner grip (hidden on mobile, where the
            card is full-bleed). */}
        <div
          className="absolute left-0 top-0 z-10 hidden h-5 w-5 cursor-nwse-resize sm:block group"
          onMouseDown={handleResizeStart}
          title={t("common.aiManagement.resize", "Drag to resize")}
          data-testid="ai-assistant-resize"
        >
          <div className="absolute left-1.5 top-1.5 h-2.5 w-2.5 rounded-tl-md border-l-2 border-t-2 border-gray-300 transition-colors group-hover:border-violet-500" />
        </div>

        {/* Chat — the only surface in the floating card. It owns the single
            (merged) header, including the expand-to-/ai and close actions. */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <SidePanelChatView
            open={visible}
            onClose={togglePanel}
            showExpand={aiPageEnabled}
          />
        </div>
      </div>

      {/* Backdrop when open on mobile (the card is near-full-bleed there) */}
      {visible && (
        <div
          className="fixed inset-0 bg-black/20 z-[95] sm:hidden"
          onClick={togglePanel}
        />
      )}

      {/* Overlay during resize */}
      {isResizing && <div className="fixed inset-0 z-[99] bg-transparent" />}
    </>
  );
}

export function AIManagementPanel({
  suppressed = false,
}: { suppressed?: boolean } = {}) {
  const user = useUserOptional();
  if (!user) return null;
  return <ActivityPanelContent suppressed={suppressed} />;
}

// Floating launcher (STK-356): a fixed bottom-right button that toggles the
// AI card. Rendered once globally; it owns no panel markup — it only flips the
// shared open flag (localStorage + `activityPanelToggle` event) the card
// listens to. Route / mobile-app gating lives at the mount site (PageContent);
// here we only self-hide while the card is open (it covers the same corner).
function ActivityPanelButtonContent() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Load the open flag on mount.
  useEffect(() => {
    setIsOpen(getPanelOpen());
  }, []);

  // Return focus to the launcher when the card closes (it is this button that
  // opened the dialog). Only on a real open→closed transition, so we don't
  // steal focus on initial mount or while the card is open.
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      buttonRef.current?.focus();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  // Listen for toggle events (same tab, e.g. the card's close button).
  useEffect(() => {
    const handleToggle = (e: CustomEvent<boolean>) => {
      setIsOpen(e.detail);
    };
    window.addEventListener(
      "activityPanelToggle",
      handleToggle as EventListener,
    );
    return () =>
      window.removeEventListener(
        "activityPanelToggle",
        handleToggle as EventListener,
      );
  }, []);

  // Listen for the open flag changing in other tabs.
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === PANEL_OPEN_KEY) {
        setIsOpen(e.newValue === "true");
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const togglePanel = useCallback(() => {
    const next = !isOpen;
    setIsOpen(next);
    savePanelOpen(next);
    // Dispatch event asynchronously to avoid setState during render
    queueMicrotask(() => {
      window.dispatchEvent(
        new CustomEvent("activityPanelToggle", { detail: next }),
      );
    });
  }, [isOpen]);

  // Hide the launcher while the card is open (it sits on the same corner).
  // Placed after all hooks so hook order stays stable.
  if (isOpen) return null;

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={togglePanel}
      title={t("common.aiManagement.openAssistant")}
      aria-label={t("common.aiManagement.openAssistant")}
      data-testid="floating-ai-button"
      className={cn(
        "fixed bottom-4 right-4 z-[90] flex h-14 w-14 items-center justify-center rounded-full",
        "bg-violet-600 text-white shadow-lg ring-1 ring-violet-700/20",
        "transition-all hover:bg-violet-700 hover:shadow-xl active:scale-95",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2",
      )}
    >
      <Bot className="h-6 w-6" />
    </button>
  );
}

export function AIManagementPanelButton() {
  const user = useUserOptional();
  if (!user) return null;
  return <ActivityPanelButtonContent />;
}
